/**
 * SeoHead
 *
 * Per-route SEO metadata via react-helmet-async. Updates document.title,
 * meta description, canonical, and Open Graph tags client-side on
 * navigation. Provides clean tab titles and improves social-share
 * accuracy for users who reach a specific route via direct URL.
 *
 * Note: SPA cannot natively give crawlers different OG per route — that
 * requires SSR or an edge worker. The static index.html holds optimal
 * site-wide defaults; this component layers per-route refinements on top.
 */
import { Helmet } from "react-helmet-async";

const SITE = "https://elearningsaham.my";
const BRAND = "eLearningsaham by Maahad Bursa";
const DEFAULT_OG = `${SITE}/og-image.png?v=6`;

export function SeoHead({
    title,
    description,
    path = "/",
    image,
    type = "website",
    noindex = false,
}) {
    const fullTitle = title ? `${title} · ${BRAND}` : `${BRAND} | Shariah-Compliant Stock Learning Platform`;
    const desc = description ||
        "eLearningsaham is a professional stock learning portal by Maahad Bursa for registered students to access class recordings, learning modules, trading journals, events, and educational resources in one secure platform.";
    const url = `${SITE}${path.startsWith("/") ? path : "/" + path}`;
    const ogImage = image || DEFAULT_OG;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="title" content={fullTitle} />
            <meta name="description" content={desc} />
            <link rel="canonical" href={url} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}

            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />
        </Helmet>
    );
}

// Per-route SEO presets — single source of truth for public marketing routes.
export const SEO_PRESETS = {
    home: {
        title: null, // Use full brand title
        description:
            "eLearningsaham is a professional stock learning portal by Maahad Bursa for registered students to access class recordings, learning modules, trading journals, events, and educational resources in one secure platform.",
        path: "/",
    },
    login: {
        title: "Sign In",
        description:
            "Sign in to the eLearningsaham portal to access your class recordings, modules, notes, and learning progress.",
        path: "/login",
    },
    register: {
        title: "Create Account",
        description:
            "Create your eLearningsaham account to enrol in Maahad Bursa stock-learning classes.",
        path: "/register",
    },
    forgotPassword: {
        title: "Forgot Password",
        description:
            "Reset your eLearningsaham account password securely.",
        path: "/forgot-password",
        noindex: true,
    },
    createPassword: {
        title: "Create Password",
        description:
            "First-time login — create your eLearningsaham account password.",
        path: "/create-password",
        noindex: true,
    },
};
